import React from "react";
import { Breadcrumb, Layout, Card, Row, Col, Button, theme, Spin, Alert, Tag, Avatar, Collapse, Space, Tabs } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { PlusOutlined, EyeOutlined, PlayCircleOutlined, PauseCircleOutlined, BarChartOutlined, FileTextOutlined, TeamOutlined, LineChartOutlined } from "@ant-design/icons";
import { useGetSurveysQuery, useGetBrandsQuery, useGetSurveyCohortsQuery } from "../services/nestjs-base-api";
import { ISurvey, IBrand, ISurveyCohort } from "../services/nestjs-base-api/types";
import { renderFlagIcon, getLanguageName, getCountryName } from "../constants/countries";
import CohortManagement from "../components/Cohorts/CohortManagement";
import ResponseVolumeTab from "../components/Surveys/ResponseVolumeTab";

const { Content } = Layout;

const SurveysPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Get active tab from URL or default to surveys
    const activeTab = searchParams.get('tab') || 'surveys';

    const handleTabChange = (key: string) => {
        setSearchParams({ tab: key });
    };

    // Fetch surveys from API
    const { data: surveys, isLoading, error } = useGetSurveysQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch brands for brand name lookup
    const { data: brands } = useGetBrandsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch cohorts for cohort name lookup
    const { data: cohorts } = useGetSurveyCohortsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    const getBrandColor = (brandId: number): string => {
        const brand = brands?.find((b: IBrand) => b.id === brandId);
        return brand?.primaryColor || '#4338ca';
    };

    const getCohortName = (cohortId: number): string => {
        const cohort = cohorts?.find((c: ISurveyCohort) => c.id === cohortId);
        return cohort?.displayName || 'Unknown Cohort';
    };

    const getCohortColor = (cohortId: number): string => {
        // Use a distinct color palette for cohorts
        const colors = [
            'blue',      // ID 1
            'green',     // ID 2
            'orange',    // ID 3
            'purple',    // ID 4
            'cyan',      // ID 5
            'magenta',   // ID 6
            'volcano',   // ID 7
            'gold',      // ID 8
            'lime',      // ID 9
            'geekblue',  // ID 10+
        ];
        // Use modulo to cycle through colors if there are more cohorts
        return colors[(cohortId - 1) % colors.length];
    };

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active': return 'green';
            case 'draft': return 'default';
            case 'paused': return 'orange';
            case 'completed': return 'blue';
            default: return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <PlayCircleOutlined />;
            case 'paused': return <PauseCircleOutlined />;
            default: return <EyeOutlined />;
        }
    };

    const handleCreateSurvey = () => {
        navigate('/dashboard/surveys/create');
    };

    const handleViewSurvey = (surveyHash: string) => {
        navigate(`/dashboard/surveys/${surveyHash}`);
    };

    const handleViewResults = (surveyHash: string) => {
        navigate(`/dashboard/surveys/${surveyHash}/results`);
    };

    const handleViewQuestionTemplates = () => {
        navigate('/dashboard/surveys/question-templates');
    };

    // Check if user can create surveys (has brands and cohorts)
    // Since we support multiple surveys per cohort (one per country), always allow if we have brands and cohorts
    const hasAvailableCohorts = React.useMemo(() => {
        if (!brands || !cohorts) return false; // Can't create if no brands or cohorts

        // Get active cohorts only
        const activeCohorts = cohorts.filter((c: ISurveyCohort) => c.isActive !== false);

        // Can create surveys if we have at least one brand and one active cohort
        // Users can now create surveys for same cohort in different countries
        return brands.length > 0 && activeCohorts.length > 0;
    }, [brands, cohorts]);

    // Separate surveys by status
    const activeSurveys = surveys?.filter((survey: ISurvey) => survey.status !== 'completed') || [];
    const completedSurveys = surveys?.filter((survey: ISurvey) => survey.status === 'completed') || [];

    // Group surveys by country
    const groupSurveysByCountry = (surveyList: ISurvey[]) => {
        const grouped = surveyList.reduce((acc, survey) => {
            const country = survey.countryCode || 'Unknown';
            if (!acc[country]) {
                acc[country] = [];
            }
            acc[country].push(survey);
            return acc;
        }, {} as Record<string, ISurvey[]>);

        // Sort countries alphabetically
        return Object.keys(grouped)
            .sort()
            .map(countryCode => ({
                countryCode,
                surveys: grouped[countryCode]
            }));
    };

    const activeSurveysByCountry = groupSurveysByCountry(activeSurveys);
    const completedSurveysByCountry = groupSurveysByCountry(completedSurveys);

    const renderSurveyCard = (survey: ISurvey) => {
        const cohortId = survey.cohortId || (survey as any).cohort?.id;
        return (
        <Col xs={24} sm={12} lg={8} xl={6} key={survey.id}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                            <Avatar
                                size="small"
                                style={{ backgroundColor: getBrandColor(survey.brandId) }}
                            >
                                {survey.brand?.name}
                            </Avatar>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {survey.name}
                                </div>
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {survey.brand?.name}
                                </div>
                            </div>
                        </div>
                        <Tag 
                            color={getStatusColor(survey.status)} 
                            icon={getStatusIcon(survey.status)}
                            style={{ flexShrink: 0, marginLeft: 8 }}
                        >
                            {intl.formatMessage({ id: `surveys.status.${survey.status}` })}
                        </Tag>
                    </div>
                }
                actions={[
                    <Button 
                        key="view"
                        type="text" 
                        icon={<EyeOutlined />}
                        onClick={() => handleViewSurvey(survey.hash)}
                    >
                        {intl.formatMessage({ id: 'action.view' })}
                    </Button>,
                    survey.status === 'active' || survey.status === 'completed' ? (
                        <Button 
                            key="results"
                            type="text" 
                            icon={<BarChartOutlined />}
                            onClick={() => handleViewResults(survey.hash)}
                        >
                            {intl.formatMessage({ id: 'action.results' })}
                        </Button>
                    ) : null
                ].filter(Boolean)}
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewSurvey(survey.hash)}
            >
                <div style={{ minHeight: '80px' }}>
                    {cohortId && (
                        <div style={{ marginBottom: 12 }}>
                            <Tag color={getCohortColor(cohortId)} style={{ fontSize: '11px' }}>
                                {getCohortName(cohortId)}
                            </Tag>
                        </div>
                    )}

                    {(survey.countryCode || survey.languageCode) && (
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {survey.countryCode && renderFlagIcon(survey.countryCode, 16)}
                            {survey.languageCode && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {getLanguageName(survey.languageCode)}
                                </span>
                            )}
                        </div>
                    )}

                    {survey.description && (
                        <p style={{
                            color: '#6b7280',
                            fontSize: '13px',
                            marginBottom: 12,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {survey.description}
                        </p>
                    )}

                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        <div>
                            {intl.formatMessage({ id: 'surveys.created' })}: {new Date(survey.createdAt).toLocaleDateString()}
                        </div>
                        {survey.settings?.questionsPerResponse && (
                            <div>
                                {survey.settings.questionsPerResponse} {intl.formatMessage({ id: 'surveys.questionsPerResponse' })}
                            </div>
                        )}
                        {survey.responseCount !== undefined && (
                            <div>
                                {survey.responseCount} {survey.responseCount === 1 ? 'response' : 'responses'}
                            </div>
                        )}
                        {survey.accessCount !== undefined && (
                            <div>
                                {survey.accessCount} {intl.formatMessage({ id: 'surveys.linkAccesses' })}
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </Col>
        );
    };

    if (isLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin size="large" />
                </div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ padding: 24 }}>
                    <Alert 
                        type="error" 
                        message="Failed to load surveys" 
                        description="There was an error loading your surveys. Please try again later." 
                    />
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user?.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    { title: intl.formatMessage({ id: 'section.surveys' }) }
                ]}
            />
            
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <div style={{ marginBottom: 24 }}>
                    <h2 style={{ marginBottom: 8 }}>{intl.formatMessage({ id: 'section.surveys' })}</h2>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                        {intl.formatMessage({ id: 'surveys.subtitle' })}
                    </p>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={[
                        {
                            key: 'surveys',
                            label: (
                                <span>
                                    <FileTextOutlined style={{ marginRight: 8 }} />
                                    Surveys
                                </span>
                            ),
                            children: (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                        <Space>
                                            <Button
                                                icon={<FileTextOutlined />}
                                                onClick={handleViewQuestionTemplates}
                                            >
                                                Question Templates
                                            </Button>
                                            {hasAvailableCohorts && (
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={handleCreateSurvey}
                                                >
                                                    Create Survey
                                                </Button>
                                            )}
                                        </Space>
                                    </div>
                
                {!surveys || surveys.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <div style={{ fontSize: '48px', color: '#d1d5db', marginBottom: 16 }}>
                            📋
                        </div>
                        <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: 16 }}>
                            {intl.formatMessage({ id: 'surveys.noSurveys' })}
                        </p>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={handleCreateSurvey}
                        >
                            {intl.formatMessage({ id: 'surveys.createFirstSurvey' })}
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Active Surveys - Grouped by Country */}
                        {activeSurveys.length > 0 && (
                            <>
                                <div style={{ marginBottom: 16 }}>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                        Active Surveys ({activeSurveys.length})
                                    </h3>
                                </div>
                                <Collapse
                                    ghost
                                    defaultActiveKey={activeSurveysByCountry.map(g => g.countryCode)}
                                    style={{ marginBottom: 32 }}
                                    items={activeSurveysByCountry.map(group => ({
                                        key: group.countryCode,
                                        label: (
                                            <span style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {renderFlagIcon(group.countryCode, 20)}
                                                {getCountryName(group.countryCode) || group.countryCode}
                                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                                    {group.surveys.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: (
                                            <Row gutter={[16, 16]}>
                                                {group.surveys.map(renderSurveyCard)}
                                            </Row>
                                        ),
                                    }))}
                                />
                            </>
                        )}

                        {/* Completed Surveys - Grouped by Country */}
                        {completedSurveys.length > 0 && (
                            <>
                                <div style={{ marginBottom: 16 }}>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                        Completed Surveys ({completedSurveys.length})
                                    </h3>
                                </div>
                                <Collapse
                                    ghost
                                    items={completedSurveysByCountry.map(group => ({
                                        key: `completed-${group.countryCode}`,
                                        label: (
                                            <span style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                {renderFlagIcon(group.countryCode, 20)}
                                                {getCountryName(group.countryCode) || group.countryCode}
                                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                                    {group.surveys.length}
                                                </Tag>
                                            </span>
                                        ),
                                        children: (
                                            <Row gutter={[16, 16]}>
                                                {group.surveys.map(renderSurveyCard)}
                                            </Row>
                                        ),
                                    }))}
                                />
                            </>
                        )}

                        {/* Show empty state only if no surveys exist at all */}
                        {activeSurveys.length === 0 && completedSurveys.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                <div style={{ fontSize: '48px', color: '#d1d5db', marginBottom: 16 }}>
                                    📋
                                </div>
                                <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: 16 }}>
                                    {intl.formatMessage({ id: 'surveys.noSurveys' })}
                                </p>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={handleCreateSurvey}
                                >
                                    {intl.formatMessage({ id: 'surveys.createFirstSurvey' })}
                                </Button>
                            </div>
                        )}
                    </>
                )}
                                </>
                            ),
                        },
                        {
                            key: 'response-volume',
                            label: (
                                <span>
                                    <LineChartOutlined style={{ marginRight: 8 }} />
                                    {intl.formatMessage({ id: 'responseVolume.tab', defaultMessage: 'Response Volume' })}
                                </span>
                            ),
                            children: (
                                <ResponseVolumeTab
                                    countryCodes={Array.from(
                                        new Set(
                                            (surveys || [])
                                                .map((s: ISurvey) => s.countryCode)
                                                .filter(Boolean) as string[],
                                        ),
                                    )}
                                />
                            ),
                        },
                        {
                            key: 'cohorts',
                            label: (
                                <span>
                                    <TeamOutlined style={{ marginRight: 8 }} />
                                    Customer Cohorts
                                </span>
                            ),
                            children: <CohortManagement />,
                        },
                    ]}
                />
            </div>
        </Content>
    );
};

export default SurveysPage;